import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribeToInstrumentPrices, unsubscribeFromInstrumentPrices } from '../../../services/websocketApi/prices';
import websocketService from '../../../services/websocket';

export default function usePriceSubscription(isWebSocketReady, socket, pageInstruments, setPageInstruments) {
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(false);
  const subscribedInstrumentsRef = useRef([]);

  // Subscribe to price updates for currently displayed instruments
  const subscribeToPriceUpdates = useCallback((instruments) => {
    if (!isWebSocketReady || !instruments || instruments.length === 0) return;
    
    // Format instruments properly for subscription
    const formattedInstruments = instruments
      .filter(instr => instr.originalId && !instr.isPlaceholder)
      .map(instr => {
        // Parse the originalId which is in format "0000001.FX.AUDCAD"
        const originalId = instr.originalId;
        
        // Split by dots to extract components
        const parts = originalId.split('.');
        
        // Default values
        let id, type, symbol;
        
        if (parts.length >= 3) {
          // For format like "0000001.FX.AUDCAD"
          id = parts[0];                // e.g., "0000001"
          type = parts[1];              // e.g., "FX"
          symbol = parts.slice(2).join('.'); // e.g., "AUDCAD" (handles if symbol has dots)
        } else if (parts.length === 2) {
          // For format like "FX.AUDCAD"
          id = originalId;
          type = parts[0];
          symbol = parts[1];
        } else {
          // Fallback for unexpected format
          id = originalId;
          
          // Determine type based on category
          if (instr.category) {
            const category = instr.category.toLowerCase();
            if (category === 'crypto' || category === 'cryptocurrency') {
              type = 'CRYPTO';
            } else if (category === 'stock' || category === 'stocks' || category === 'equity') {
              type = 'STOCK';
            } else if (category === 'indices' || category === 'index') {
              type = 'INDEX';
            } else if (category === 'commodity' || category === 'commodities') {
              type = 'COMMODITY';
            } else if (category === 'forex' || category === 'fx') {
              type = 'FX';
            } else {
              type = 'FX'; // Default to FX
            }
          } else {
            type = 'FX'; // Default to FX
          }
          
          symbol = originalId;
        }
        
        return {
          id,
          type,
          symbol
        };
      });
    
    if (formattedInstruments.length === 0) {
      return;
    }
    
    // Subscribe to price updates for these instruments
    subscribeToInstrumentPrices(formattedInstruments)
      .catch(err => console.error('Failed to subscribe to price updates:', err));
    
    // Set up message handler for price updates
    const handlePriceUpdate = (event) => {
      try {
        const data = typeof event === 'string' ? JSON.parse(event) : event.data ? JSON.parse(event.data) : event;
        
        // Check if this is a price update message
        if (data && (data.type === 'price-update' || data.type === 'instrument-price-update' || data.type === 'feed')) {
          // Handle feed message format with MarketDataEntries
          if (data.type === 'feed' && data.data && data.data.MarketDataEntries) {
            const instrumentData = data.data;
            const symbol = instrumentData.symbol;
            
            // Extract bid and ask prices from MarketDataEntries
            let bidPrice = null;
            let askPrice = null;
            
            // Find the bid and ask entries
            for (const entry of instrumentData.MarketDataEntries) {
              if (entry.Type === 'Bid' && entry.Price) {
                bidPrice = parseFloat(entry.Price);
              } else if (entry.Type === 'Ask' && entry.Price) {
                askPrice = parseFloat(entry.Price);
              }
            }
            
            // If we have both bid and ask, calculate the mid price
            let newPrice = null;
            if (bidPrice !== null && askPrice !== null) {
              newPrice = (bidPrice + askPrice) / 2;
            } else if (bidPrice !== null) {
              newPrice = bidPrice;
            } else if (askPrice !== null) {
              newPrice = askPrice;
            }
            
            // We don't have change data in this feed, so only update price
            if (symbol && newPrice !== null) {
              // Update the instrument in our state
              setPageInstruments(prevInstruments => 
                prevInstruments.map(instr => {
                  // Check if this update is for this instrument
                  const matchesSymbol = 
                    // Exact match
                    instr.originalId === symbol || 
                    // Match when originalId includes the symbol
                    (instr.originalId && instr.originalId.includes(`.${symbol}`)) ||
                    // Match when symbol includes the originalId
                    (instr.originalId && symbol.includes(instr.originalId)) ||
                    // Match on the instrument part (after second dot)
                    (symbol && symbol.includes('.') && instr.originalId && instr.originalId.includes('.') && 
                      ((symbol.split('.').length > 2 && instr.originalId.split('.').length > 2) && 
                      symbol.split('.')[2] === instr.originalId.split('.')[2])) ||
                    // Match when the symbol format is "0000021.FX.GBPAUD" but originalId might be "GBPAUD"
                    (symbol && symbol.includes('.') && symbol.split('.').length > 2 && 
                      instr.originalId && !instr.originalId.includes('.') && 
                      symbol.split('.')[2] === instr.originalId);
                  
                  if (matchesSymbol) {
                    return { 
                      ...instr, 
                      price: newPrice,
                      // Store bid/ask in case we need them later
                      bidPrice: bidPrice,
                      askPrice: askPrice
                    };
                  }
                  return instr;
                })
              );
            }
          } 
          // Handle other price update formats
          else {
            const price = data.data || data;
            // Price might be in data.data, or directly in data
            const symbol = price.symbol || price.instrumentId || price.id;
            const newPrice = typeof price.price === 'number' ? price.price : null;
            const newChange = typeof price.change === 'number' ? price.change : 
                            typeof price['24hour-change'] === 'number' ? price['24hour-change'] : null;
            
            if (symbol && (newPrice !== null || newChange !== null)) {
              // Update the instrument in our state
              setPageInstruments(prevInstruments => 
                prevInstruments.map(instr => 
                  (instr.originalId === symbol || 
                  // Also check if originalId contains the symbol (for cases like "0000001.FX.AUDCAD" with symbol "AUDCAD")
                  (instr.originalId && instr.originalId.includes(`.${symbol}`)) ||
                  // Or check if symbol is at the end of originalId (for cases with full ID format)
                  (instr.originalId && instr.originalId.endsWith(`.${symbol}`)))
                    ? { 
                        ...instr, 
                        price: newPrice !== null ? newPrice : instr.price,
                        change: newChange !== null ? newChange : instr.change 
                      }
                    : instr
                )
              );
            }
          }
        }
      } catch (err) {
        console.error('Error processing price update message:', err);
      }
    };
    
    // Add message event listener for price updates
    if (socket && socket.addEventListener) {
      socket.addEventListener('message', handlePriceUpdate);
    } else if (socket && socket.on) {
      socket.on('instrument-price-update', handlePriceUpdate);
      socket.on('feed', handlePriceUpdate);
    }
    
    // Connect directly to the websocketService for price-feed events
    const priceFeedHandler = (data) => {
      if (data && data.symbol) {
        const { symbol, bid, ask } = data;
        
        // Calculate mid price from bid and ask
        let newPrice = null;
        if (bid !== null && ask !== null) {
          newPrice = (parseFloat(bid) + parseFloat(ask)) / 2;
        } else if (bid !== null) {
          newPrice = parseFloat(bid);
        } else if (ask !== null) {
          newPrice = parseFloat(ask);
        }
        
        if (newPrice !== null) {
          // Update the instrument in our state
          setPageInstruments(prevInstruments => {
            const updatedInstruments = prevInstruments.map(instr => {
              // Check if this update is for this instrument
              const matchesSymbol = 
                // Exact match
                instr.originalId === symbol || 
                // Match when originalId includes the symbol
                (instr.originalId && instr.originalId.includes(`.${symbol}`)) ||
                // Match when symbol includes the originalId
                (instr.originalId && symbol.includes(instr.originalId)) ||
                // Match on the instrument part (after second dot)
                (symbol && symbol.includes('.') && instr.originalId && instr.originalId.includes('.') && 
                  ((symbol.split('.').length > 2 && instr.originalId.split('.').length > 2) && 
                  symbol.split('.')[2] === instr.originalId.split('.')[2])) ||
                // Match when the symbol format is "0000021.FX.GBPAUD" but originalId might be "GBPAUD"
                (symbol && symbol.includes('.') && symbol.split('.').length > 2 && 
                  instr.originalId && !instr.originalId.includes('.') && 
                  symbol.split('.')[2] === instr.originalId);
              
              if (matchesSymbol) {
                return { 
                  ...instr, 
                  price: newPrice,
                  bidPrice: parseFloat(bid),
                  askPrice: parseFloat(ask)
                };
              }
              return instr;
            });
            
            return updatedInstruments;
          });
        }
      }
    };
    
    // Register the direct handler
    websocketService.on('price-feed', priceFeedHandler);
    
    // Return cleanup function
    return () => {
      // Unsubscribe from price updates
      if (formattedInstruments.length > 0) {
        // Create an array of instrument strings in format "id.type.symbol"
        const instrumentStrings = formattedInstruments.map(instr => 
          `${instr.id}.${instr.type}.${instr.symbol}`
        );
        
        // Use the simplified format for unsubscription
        unsubscribeFromInstrumentPrices(instrumentStrings)
          .catch(err => console.error('Failed to unsubscribe from price updates:', err));
      }
      
      // Remove event listener
      if (socket && socket.removeEventListener) {
        socket.removeEventListener('message', handlePriceUpdate);
      } else if (socket && socket.off) {
        socket.off('instrument-price-update', handlePriceUpdate);
        socket.off('price-update', handlePriceUpdate);
        socket.off('feed', handlePriceUpdate);
      }
      
      // Remove direct handler
      websocketService.off('price-feed', priceFeedHandler);
    };
  }, [isWebSocketReady, socket, setPageInstruments]);

  // Subscribe to price updates whenever relevant instruments change
  useEffect(() => {
    if (isWebSocketReady && pageInstruments.length > 0) {
      // Extract instrument IDs for comparison
      const currentInstrumentIds = pageInstruments
        .filter(instr => instr.originalId && !instr.isPlaceholder)
        .map(instr => instr.originalId)
        .sort();
      
      // Get previously subscribed instrument IDs
      const prevInstrumentIds = subscribedInstrumentsRef.current.sort();
      
      // Check if instrument set has changed
      const instrumentsChanged = 
        currentInstrumentIds.length !== prevInstrumentIds.length || 
        currentInstrumentIds.some((id, index) => id !== prevInstrumentIds[index]);
      
      // Only resubscribe if the instrument set has actually changed
      if (instrumentsChanged) {
        // Set up subscription to price updates
        const cleanup = subscribeToPriceUpdates(pageInstruments);
        
        // Update ref with current subscribed instruments
        subscribedInstrumentsRef.current = currentInstrumentIds;
        
        // Update subscription count and time for display
        setSubscriptionCount(currentInstrumentIds.length);
        setLastUpdateTime(new Date());
        
        // Trigger animation
        setSubscriptionUpdated(true);
        setTimeout(() => setSubscriptionUpdated(false), 1000);
        
        // Clean up subscription when component unmounts or instruments change
        return cleanup;
      }
    }
  }, [isWebSocketReady, pageInstruments.map(instr => instr.originalId).join(','), subscribeToPriceUpdates]);

  return {
    subscribeToPriceUpdates,
    subscriptionCount,
    lastUpdateTime,
    subscriptionUpdated,
    subscribedInstrumentsRef
  };
} 